import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatAgo, formatDateTime } from '../lib/format'
import type { Customer, Endgeraet, EndgeraeteListe } from '../lib/types'
import { ErrorBox, Loading } from './Feedback'

type Filter = '' | 'neu' | 'bekannt' | 'gelernt'

const STATUS: Record<Endgeraet['status'], { text: string; klasse: string }> = {
  neu: { text: 'neu', klasse: 'badge badge-warn' },
  bekannt: { text: 'bekannt', klasse: 'badge badge-ok' },
  gelernt: { text: 'gelernt', klasse: 'badge badge-off' },
}

/**
 * Geräte im Netz (Stufe 4.3): alle MAC-Adressen aus den ARP- und MAC-Tabellen
 * eines Kunden. Ist die Meldung eingeschaltet, bringt jede unbekannte Adresse
 * einen Alarm, bis sie als bekannt markiert ist.
 */
export function GeraeteImNetz({ customer, onSuchen }: { customer?: Customer; onSuchen: (mac: string) => void }) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<Filter>('')
  const [suche, setSuche] = useState('')

  const params = new URLSearchParams()
  if (customer) params.set('kunde', customer.id)
  if (filter) params.set('status', filter)
  if (suche.trim()) params.set('q', suche.trim())
  const liste = useQuery({
    queryKey: ['endgeraete', customer?.id ?? null, filter, suche.trim()],
    queryFn: ({ signal }) => apiGet<EndgeraeteListe>(`/endpoints?${params.toString()}`, signal),
  })

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['endgeraete'] })
    void queryClient.invalidateQueries({ queryKey: ['alerts'] })
  }
  const bestaetigen = useMutation({
    mutationFn: ({ kunde, macs, notiz }: { kunde: string; macs: string[]; notiz: string }) =>
      apiSend<{ bestaetigt: number }>('POST', `/customers/${kunde}/endgeraete/bekannt`, { macs, notiz }),
    onSuccess: neuLaden,
  })

  const markieren = (kunde: string, macs: string[]) => {
    const notiz = window.prompt(
      macs.length === 1
        ? `${macs[0]} als bekannt markieren. Notiz (optional), z. B. „Drucker Empfang“:`
        : `${macs.length} Adressen als bekannt markieren. Notiz (optional):`,
      '',
    )
    if (notiz === null) return
    bestaetigen.mutate({ kunde, macs, notiz })
  }

  const d = liste.data
  const anzahl = d?.anzahl ?? {}
  const alle = (anzahl.neu ?? 0) + (anzahl.bekannt ?? 0) + (anzahl.gelernt ?? 0)
  const darfMarkieren = can('alert.ack')
  const neueSichtbar = (d?.eintraege ?? []).filter((e) => e.status === 'neu')

  return (
    <section className="card">
      <div className="card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2 className="card-title">Geräte im Netz</h2>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {(
            [
              ['', `Alle ${alle}`],
              ['neu', `Neu ${anzahl.neu ?? 0}`],
              ['bekannt', `Bekannt ${anzahl.bekannt ?? 0}`],
              ['gelernt', `Gelernt ${anzahl.gelernt ?? 0}`],
            ] as [Filter, string][]
          ).map(([key, label]) => (
            <button
              key={key || 'alle'}
              type="button"
              className="btn btn-sm"
              aria-pressed={filter === key}
              style={
                filter === key ? { background: 'var(--acc-soft)', color: 'var(--acc)', fontWeight: 600 } : undefined
              }
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {customer && can('settings.manage') ? <Meldung customer={customer} /> : null}

      <div className="card-body row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label className="sr-only" htmlFor="endgeraete-suche">
          Geräte im Netz durchsuchen
        </label>
        <input
          id="endgeraete-suche"
          className="input grow"
          placeholder="MAC, IP oder Notiz"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        {customer && darfMarkieren && neueSichtbar.length > 1 ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={bestaetigen.isPending}
            onClick={() =>
              markieren(
                customer.id,
                neueSichtbar.map((e) => e.mac),
              )
            }
          >
            Alle {neueSichtbar.length} neuen als bekannt markieren
          </button>
        ) : null}
      </div>
      {bestaetigen.isError ? (
        <div className="card-body" style={{ color: 'var(--crit)', paddingTop: 0 }}>
          {(bestaetigen.error as Error).message}
        </div>
      ) : null}

      {liste.isPending ? (
        <Loading what="Geräte im Netz" />
      ) : liste.isError ? (
        <ErrorBox error={liste.error} what="Die Liste der Geräte im Netz" />
      ) : d && d.eintraege.length === 0 ? (
        <div className="empty">
          <strong>{filter === 'neu' ? 'Keine neuen Geräte' : 'Keine Einträge'}</strong>
          <span>
            Die Liste entsteht aus den ARP- und MAC-Tabellen der abgefragten Router und Switches; sie werden alle zehn
            Minuten gelesen.
          </span>
        </div>
      ) : d ? (
        <>
          <table className="data">
            <thead>
              <tr>
                <th>Status</th>
                <th>MAC-Adresse</th>
                <th>IP-Adresse</th>
                {customer ? null : <th>Kunde</th>}
                <th>Gesehen von</th>
                <th>Erstmals</th>
                <th>Zuletzt</th>
                <th>Notiz</th>
                {darfMarkieren ? <th aria-label="Aktion" /> : null}
              </tr>
            </thead>
            <tbody>
              {d.eintraege.map((e) => (
                <tr key={`${e.kunde_id}-${e.mac}`}>
                  <td>
                    <span className={STATUS[e.status].klasse}>{STATUS[e.status].text}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-link"
                      title="Im Port-Finder suchen"
                      onClick={() => onSuchen(e.mac)}
                    >
                      <span className="mono">{e.mac}</span>
                    </button>
                    {e.zufaellig ? (
                      <span
                        className="muted"
                        style={{ fontSize: 12 }}
                        title="Lokal verwaltete, meist zufällige Adresse"
                      >
                        {' '}
                        · zufällig
                      </span>
                    ) : null}
                  </td>
                  <td className="mono">{e.ip || '–'}</td>
                  {customer ? null : <td>{e.kunde_name}</td>}
                  <td>{e.gesehen_von || '–'}</td>
                  <td>{formatDateTime(e.erstmals)}</td>
                  <td>{formatAgo(e.zuletzt)}</td>
                  <td>
                    {e.notiz}
                    {e.bestaetigt_am ? (
                      <span className="muted" style={{ fontSize: 12, display: 'block' }}>
                        bestätigt {e.bestaetigt_von ? `von ${e.bestaetigt_von} ` : ''}am{' '}
                        {formatDateTime(e.bestaetigt_am)}
                      </span>
                    ) : null}
                  </td>
                  {darfMarkieren ? (
                    <td>
                      {e.status === 'neu' ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={bestaetigen.isPending}
                          onClick={() => markieren(e.kunde_id, [e.mac])}
                        >
                          Als bekannt markieren
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          {d.gekuerzt ? (
            <div className="card-body muted" style={{ fontSize: 13 }}>
              Es werden nur die ersten Einträge gezeigt; die Suche grenzt die Liste ein.
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

/** Schalter je Kunde: neue Geräte melden, zufällige Adressen auslassen. */
function Meldung({ customer }: { customer: Customer }) {
  const queryClient = useQueryClient()
  const aktivJetzt = customer.neue_geraete_melden ?? false
  const zufallJetzt = customer.zufaellige_mac_ignorieren ?? true
  const [aktiv, setAktiv] = useState(aktivJetzt)
  const [zufall, setZufall] = useState(zufallJetzt)
  const speichern = useMutation({
    mutationFn: () =>
      apiSend<Customer>('PUT', `/customers/${customer.id}/neue-geraete`, {
        melden: aktiv,
        zufaellige_ignorieren: zufall,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      void queryClient.invalidateQueries({ queryKey: ['endgeraete'] })
    },
  })
  const ab = customer.neue_geraete_ab
  const lernt = aktivJetzt && ab && new Date(ab).getTime() > Date.now()

  return (
    <form
      className="card-body row"
      style={{ gap: 16, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid var(--border)' }}
      onSubmit={(e) => {
        e.preventDefault()
        speichern.mutate()
      }}
    >
      <label className="row" style={{ gap: 6 }}>
        <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
        Neue Geräte im Netz von {customer.name} melden
      </label>
      <label className="row" style={{ gap: 6 }}>
        <input type="checkbox" checked={zufall} disabled={!aktiv} onChange={(e) => setZufall(e.target.checked)} />
        Zufällige MAC-Adressen nicht melden
      </label>
      <button
        type="submit"
        className="btn btn-sm"
        disabled={speichern.isPending || (aktiv === aktivJetzt && zufall === zufallJetzt)}
      >
        Meldung speichern
      </button>
      <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 240 }}>
        {lernt
          ? `Lernt bis ${formatDateTime(ab)}, was schon im Netz ist; danach bringt jede unbekannte Adresse einen Alarm.`
          : aktivJetzt
            ? 'Jede unbekannte Adresse bringt einen Alarm, bis sie als bekannt markiert ist.'
            : 'Nach dem Einschalten lernt die Zentrale einen Tag lang, was schon im Netz ist.'}
      </span>
      {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
    </form>
  )
}
